// Fase 15A — Aziende clienti: lista (search/filter/paginazione, solo metadata
// amministrativi, punto 75) + wizard di creazione condiviso (CompanyWizardDialog,
// lo stesso offerto ai Consulenti in modalità self-assign).
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Plus, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CompanyWizardDialog } from "@/components/CompanyWizardDialog";

const COMPANY_STATUS_STYLE = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  PENDING_SETUP: "bg-amber-50 text-amber-800 border-amber-300",
  SUSPENDED: "bg-red-50 text-red-800 border-red-300",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function PlatformCompanies() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [wizard, setWizard] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState(null);
  const limit = 20;

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/platform/companies", {
        params: { search: search || undefined, status: status || undefined,
                  skip, limit } });
      setData(d);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [search, status, skip]);

  useEffect(() => { load(); }, [load]);

  const hardDelete = async () => {
    try {
      const { data: r } = await api.delete(
        `/admin/platform/companies/${deleteTarget.id}`);
      toast.success(`${deleteTarget.denominazione} eliminata definitivamente`, {
        description: `${r.documents_removed} record rimossi, ${r.user_accounts_removed} account eliminati.` });
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="companies-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="companies-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-companies-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Leaf className="w-7 h-7 text-green-800" aria-hidden="true" /> Aziende clienti
        </h1>
        <Button onClick={() => setWizard(true)} className="bg-green-700 hover:bg-green-800"
          data-testid="company-wizard-open">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nuovo Cliente
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
          placeholder="Cerca per nome, P.IVA o CF…" className="w-64 h-9"
          data-testid="companies-search-input" />
        <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
          <SelectTrigger className="w-44 h-9" data-testid="companies-status-filter">
            <SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            {["ACTIVE", "PENDING_SETUP", "SUSPENDED", "ARCHIVED"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="companies-list">
        {data.items.length === 0 && (
          <p className="text-sm text-slate-500">Nessuna azienda trovata.</p>)}
        {data.items.map((c) => (
          <div key={c.id} className="rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-3"
            data-testid={`company-${c.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">{c.denominazione}
                {c.is_demo && <Badge variant="outline" className="ml-2 text-[9px] bg-amber-50 text-amber-800 border-amber-300">DEMO</Badge>}
              </p>
              <p className="text-[11px] text-slate-500">
                {c.partita_iva || "—"} · {c.citta || c.nazione || "—"} ·
                {" "}{c.consultants_count} consulenti · {c.users_count} utenti
              </p>
            </div>
            <Badge variant="outline" className={COMPANY_STATUS_STYLE[c.status || "ACTIVE"]}
              data-testid={`company-status-${c.id}`}>{c.status || "ACTIVE"}</Badge>
            <Link to={`/admin/platform/aziende/${c.id}`}
              className="text-xs text-green-700 hover:underline"
              data-testid={`company-detail-${c.id}`}>Gestisci →</Link>
            {!c.is_demo && (
              <Button size="sm" variant="outline"
                className="h-7 text-xs text-red-700 border-red-200 hover:bg-red-50"
                onClick={() => setDeleteTarget(c)}
                data-testid={`company-delete-${c.id}`}>
                <Trash2 className="w-3 h-3" aria-hidden="true" /></Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Button variant="outline" size="sm" disabled={skip === 0}
          onClick={() => setSkip(Math.max(0, skip - limit))}
          data-testid="companies-prev"><ChevronLeft className="w-3 h-3" aria-hidden="true" /></Button>
        <span data-testid="companies-page-info">{skip + 1}–{Math.min(skip + limit, data.total)} di {data.total}</span>
        <Button variant="outline" size="sm" disabled={skip + limit >= data.total}
          onClick={() => setSkip(skip + limit)}
          data-testid="companies-next"><ChevronRight className="w-3 h-3" aria-hidden="true" /></Button>
      </div>

      <CompanyWizardDialog open={wizard} onOpenChange={setWizard}
        mode="platform" onCreated={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="company-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente {deleteTarget?.denominazione}?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati in modo irreversibile l'azienda e TUTTI i dati collegati:
              questionari, periodi, documenti, report, utenti aziendali e audit.
              L'operazione non è annullabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="company-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={hardDelete}
              data-testid="company-delete-confirm">Elimina definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
